import {
	CHARADE_EMOJI,
	CHARADE_PICKS,
	type CharadesState,
	LADDER_RUNGS,
	type PlayerIndex,
	type TypingLadderState,
} from "@playpal/core";
import { Button } from "../components/ui";

interface BoardProps<S> {
	state: S;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
}

// --- Typing Ladder --------------------------------------------------------

export function TypingLadderBoard({ state, seat, onMove }: BoardProps<TypingLadderState>) {
	const them: PlayerIndex = seat === 0 ? 1 : 0;
	const spelling = state.phase === "spelling" && !state.result;
	const mine = state.progress[seat];

	return (
		<div className="board-frame board-lg text-center">
			<p className="mb-3 text-lg font-semibold">
				Rungs — you {state.scores[seat]}, them {state.scores[them]} · first to {LADDER_RUNGS}
			</p>

			<div className="mb-4 flex justify-center gap-1">
				{state.word.split("").map((letter, index) => (
					<span
						key={`${letter}-${index}`}
						className={`flex h-14 w-11 items-center justify-center rounded-xl text-2xl font-black uppercase shadow-inner ${
							index < mine ? "bg-emerald-400 text-emerald-950" : "bg-white/70 dark:bg-white/10"
						}`}
					>
						{index < mine ? letter : "·"}
					</span>
				))}
			</div>

			<p className="mb-4 text-3xl font-black tracking-widest uppercase">{state.word}</p>

			<div className="flex flex-wrap justify-center gap-2">
				{state.tiles.map((letter, tile) => (
					<button
						key={`${letter}-${tile}`}
						type="button"
						disabled={!spelling}
						aria-label={`Letter ${letter}`}
						onClick={() => onMove({ tile })}
						className="h-14 w-14 rounded-2xl bg-white text-2xl font-black text-indigo-950 uppercase shadow-md transition enabled:hover:-translate-y-1"
					>
						{letter}
					</button>
				))}
			</div>

			<p className="mt-4 h-6 font-semibold" aria-live="polite">
				{state.lastNote ?? (spelling ? "Spell it, in order!" : "Next word coming up…")}
			</p>
			<p className="mt-1 text-sm opacity-70">They're {state.progress[them]} letters in.</p>
		</div>
	);
}

// --- Emoji Charades -------------------------------------------------------

export function CharadesBoard({ state, seat, onMove }: BoardProps<CharadesState>) {
	const acting = seat === state.actor;
	const revealing = state.phase === "reveal";

	return (
		<div className="board-frame board-lg text-center">
			<p className="mb-3 text-lg font-semibold">
				Round {state.round + 1} of 6 · {state.scores[0]} – {state.scores[1]}
			</p>

			{state.clues.length > 0 && (
				<p className="mb-4 text-5xl" role="img" aria-label="The clues so far">
					{state.clues.map((clue) => CHARADE_EMOJI[clue]).join(" ")}
				</p>
			)}

			{state.phase === "acting" &&
				(acting ? (
					<>
						<p className="mb-1 text-sm font-bold opacity-70">Your secret is</p>
						<p className="mb-4 text-2xl font-black">{state.secret}</p>
						<p className="mb-3 text-sm opacity-70">
							Pick {CHARADE_PICKS} emoji to act it out ({state.clues.length}/{CHARADE_PICKS})
						</p>
						<div className="mb-4 grid grid-cols-8 gap-1">
							{CHARADE_EMOJI.map((emoji, clue) => (
								<button
									key={emoji}
									type="button"
									aria-label={`Use ${emoji}`}
									onClick={() => onMove({ clue })}
									className="rounded-xl bg-white/70 py-2 text-2xl transition hover:scale-110 dark:bg-white/10"
								>
									{emoji}
								</button>
							))}
						</div>
						{state.clues.length > 0 && (
							<Button onClick={() => onMove({ done: true })}>That's my clue!</Button>
						)}
					</>
				) : (
					<p className="text-xl font-semibold">Your friend is choosing their emoji…</p>
				))}

			{state.phase === "guessing" &&
				(acting ? (
					<p className="text-xl font-semibold">Waiting for their guess…</p>
				) : (
					<>
						<p className="mb-3 text-lg font-semibold">What are they acting out?</p>
						<div className="grid gap-3">
							{state.options.map((option, guess) => (
								<button
									key={option}
									type="button"
									onClick={() => onMove({ guess })}
									className="rounded-2xl bg-white/80 px-5 py-4 text-xl font-bold shadow-md transition hover:scale-[1.02] dark:bg-white/10"
								>
									{option}
								</button>
							))}
						</div>
					</>
				))}

			{revealing && <p className="text-2xl font-black">{state.lastNote}</p>}
		</div>
	);
}
